import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAdminScopeInfo } from '../hooks/useAdminScope';
import { showAdminPath } from '../utils/adminAccess';
import { adminApi, type OrganizationViewResponse, type OrganizationDashboardResponse } from '../api/client';

/** ADM-S05 管理者ダッシュボード（API 連携） */
export default function AdminDashboard() {
  const { t } = useTranslation();
  const { getToken, isReady } = useAuth();
  const scopeInfo = useAdminScopeInfo();
  const adminScope = scopeInfo.status;
  const [org, setOrg] = useState<OrganizationViewResponse | null>(null);
  const [dashboard, setDashboard] = useState<OrganizationDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const loadData = useCallback(() => {
    if (!isReady) {
      return () => {};
    }
    const token = getToken();
    if (!token) {
      setLoading(false);
      return () => {};
    }
    setError(null);
    setLoading(true);
    let cancelled = false;
    Promise.all([
      adminApi.getOrganization(token).catch((e) => e as Error),
      adminApi.getDashboard(token).catch((e) => e as Error),
    ]).then(([orgRes, dashRes]) => {
      if (cancelled) return;
      if (orgRes instanceof Error) setOrg(null);
      else setOrg(orgRes);
      if (dashRes instanceof Error) setDashboard(null);
      else setDashboard(dashRes);
      if (orgRes instanceof Error || dashRes instanceof Error) {
        setError(t('dashboard.errorLoad'));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [getToken, isReady, t]);

  useEffect(() => {
    const cleanup = loadData();
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [loadData, retryKey]);

  const mcsAvg = dashboard?.mcs?.average_score;
  const mcsSamples = dashboard?.mcs?.sample_count ?? 0;
  const jobs = dashboard?.jobs;
  const reports = dashboard?.daily_reports;
  const activeUsers = dashboard?.users?.active ?? org?.active_users ?? null;
  const departments = org?.departments ?? [];

  if (loading) {
    return (
      <div className="page admin-dashboard">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page admin-dashboard">
      <h1>{t('admin.dashboard')}</h1>
      {/*
        拠点管理者に、いまどの範囲を見ているかを出す（#100）。範囲が空だと何も見えないので、
        「データが無い」と取り違えないよう、そのことをはっきり伝える
      */}
      {scopeInfo.status === 'restricted' && (
        <div
          className={`admin-card admin-scope-banner${scopeInfo.seesNothing ? ' is-warning' : ''}`}
          role={scopeInfo.seesNothing ? 'alert' : 'status'}
          data-testid="admin-scope-banner"
        >
          {scopeInfo.seesNothing
            ? t('admin.scopeBanner.seesNothing')
            : scopeInfo.departmentNames && scopeInfo.departmentNames.length > 0
            ? t('admin.scopeBanner.range', { names: scopeInfo.departmentNames.join('、') })
            : t('admin.scopeBanner.generic')}
        </div>
      )}

      <p className="admin-notice" role="note">
        <strong>注意：</strong> {t('admin.notice')}
      </p>

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={() => setRetryKey((k) => k + 1)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {dashboard && (
        <>
          <div className="admin-cards">
            <div className="admin-card">
              <h3>{t('admin.mcsAvg')}</h3>
              {!dashboard?.mcs?.suppressed ? (
                <div className="admin-stat">{mcsAvg != null ? Math.round(mcsAvg) : '—'}</div>
              ) : (
                <div className="admin-stat-hidden">
                  {t('admin.mcsHiddenBySuppression', {
                    count: mcsSamples,
                    threshold: dashboard?.mcs?.threshold ?? '—',
                    reason: dashboard?.mcs?.suppression_reason ?? '—',
                  })}
                </div>
              )}
              <div className="admin-stat-label">
                {dashboard.from_date && dashboard.to_date
                  ? t('admin.mcsPeriod', { from: dashboard.from_date, to: dashboard.to_date })
                  : t('admin.mcsPeriodDefault')}
              </div>
              {mcsSamples > 0 && <div className="admin-stat-label">{t('admin.samples')}: {mcsSamples}</div>}
            </div>
            <div className="admin-card">
              <h3>{t('admin.jobsSummary')}</h3>
              <div className="admin-stat">{jobs?.succeeded ?? 0}</div>
              <div className="admin-stat-label">{t('admin.jobsSuccess')} / {t('admin.jobsTotal', { count: jobs?.total ?? 0 })}</div>
              {jobs && jobs.failed > 0 && <div className="admin-stat-label admin-stat-label-error">{t('admin.jobsFailed')}: {jobs.failed}</div>}
            </div>
            <div className="admin-card">
              <h3>{t('admin.usersReports')}</h3>
              <div className="admin-stat">{activeUsers ?? '—'}</div>
              <div className="admin-stat-label">{t('admin.activeUsers')}</div>
              <div className="admin-stat-label">{t('admin.reportsCount')}: {reports?.total_reports ?? '—'}</div>
            </div>
          </div>
        </>
      )}

      {departments.length > 0 && (
        <div className="admin-card admin-departments">
          <h3>{t('admin.departments.listTitle')}</h3>
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.department')}</th>
                <th scope="col">{t('admin.status')}</th>
                <th scope="col">{t('admin.userCount')}</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((d) => (
                <tr key={d.id}>
                  <td>{d.name}</td>
                  <td>{d.status}</td>
                  <td>{d.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-card admin-menu">
        <h3>{t('admin.menu')}</h3>
        <ul className="admin-menu-list">
          {showAdminPath('/admin/prompts', adminScope) && (
            <li><Link to="/admin/prompts">{t('admin.prompts')}</Link> — {t('admin.menuPrompts')}</li>
          )}
          <li><Link to="/admin/users">{t('admin.usersManage')}</Link> — {t('admin.menuUsers')}</li>
          {showAdminPath('/admin/reports', adminScope) && (
            <li><Link to="/admin/reports">{t('admin.reports')}</Link> — {t('admin.menuReports')}</li>
          )}
          <li><Link to="/admin/weekly-reports">{t('admin.weeklyReports.menuLabel')}</Link> — {t('admin.weeklyReports.menuDesc')}</li>
          {showAdminPath('/admin/settings', adminScope) && (
            <li><Link to="/admin/settings">{t('admin.settings')}</Link></li>
          )}
          {showAdminPath('/admin/consent', adminScope) && (
            <li><Link to="/admin/consent">{t('admin.menuConsent')}</Link> — {t('admin.consentPageTitle')}</li>
          )}
          {showAdminPath('/admin/organization-mode', adminScope) && (
            <li><Link to="/admin/organization-mode">{t('admin.orgMode.menuLabel')}</Link> — {t('admin.orgMode.menuDesc')}</li>
          )}
          <li><Link to="/admin/data-requests">{t('admin.dataRequests.menuLabel')}</Link> — {t('admin.dataRequests.menuDesc')}</li>
          <li><Link to="/admin/departments">{t('admin.departments.menuLabel')}</Link> — {t('admin.departments.menuDesc')}</li>
          {/* 全社管理者専用の画面は、拠点管理者には出さない（#100。開くと 403 になるため） */}
          {showAdminPath('/admin/admin-scopes', adminScope) && (
            <li><Link to="/admin/admin-scopes">{t('admin.scopes.menuLabel')}</Link> — {t('admin.scopes.menuDesc')}</li>
          )}
          {showAdminPath('/admin/webhooks', adminScope) && (
            <li><Link to="/admin/webhooks">{t('admin.webhooks.menuLabel')}</Link> — {t('admin.webhooks.menuDesc')}</li>
          )}
        </ul>
      </div>
    </div>
  );
}
